import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import classnames from "classnames";
import AltLogo from "../images/main-logo.svg";
import HamburgerMenu from "../images/hamburger-menu.svg";
import HamburgerMenuClose from "../images/hamburger-menu-close.svg";
import { NavLink, useLocation } from "react-router-dom";
import Button from "./button";

const MOBILE_BP = 900;

const navLinkClass = ({ isActive }) =>
  classnames("nav-overlay-item", {
    "nav-overlay-item--accent": isActive,
    "nav-overlay-item--dark": !isActive,
  });

/* ── Inline contact form (shown inside the nav overlay on desktop) ── */
const NavContactForm = () => {
  const contactEmail = "casey@alvarezbranding.com";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const mailtoHref = useMemo(() => {
    const subject = `Project inquiry from ${name || "Website visitor"}`;
    const body = [`Name: ${name}`, `Email: ${email}`, "", message]
      .filter(Boolean)
      .join("\n");
    return `mailto:${contactEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }, [name, email, message]);

  return (
    <div className="nav-contact">
      <h2 className="nav-contact__title">Let's chat</h2>
      <p className="nav-contact__intro">
        Drop me a note and let's see if it's a good fit to collaborate.
      </p>

      <form
        className="nav-contact__form"
        onSubmit={(e) => {
          e.preventDefault();
          window.location.href = mailtoHref;
        }}
      >
        <div className="nav-contact__row">
          <div className="nav-contact__field">
            <label className="nav-contact__label" htmlFor="nav-contact-name">Name</label>
            <input
              id="nav-contact-name"
              className="nav-contact__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>
          <div className="nav-contact__field">
            <label className="nav-contact__label" htmlFor="nav-contact-email">Email</label>
            <input
              id="nav-contact-email"
              className="nav-contact__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </div>
        </div>

        <div className="nav-contact__field">
          <label className="nav-contact__label" htmlFor="nav-contact-msg">What are you working on?</label>
          <textarea
            id="nav-contact-msg"
            className="nav-contact__input nav-contact__textarea"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell me about your project..."
            required
          />
        </div>

        <div className="nav-contact__actions">
          <Button onClick={() => (window.location.href = mailtoHref)}>Send message</Button>
        </div>

        <p className="nav-contact__alt">
          Or email directly at{" "}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </p>
      </form>
    </div>
  );
};

/* ── Nav overlay ── */
const NavOverlay = ({ menuOpen, showContact, onNavigate, onContactClick, onBack }) => {
  return (
    <div className={classnames("nav-overlay", { "show": menuOpen, "nav-overlay--contact": showContact })}>
      {!showContact && (
        <ul className="nav-overlay__links">
          <li>
            <NavLink className={navLinkClass} to="/" end onClick={onNavigate}>Home</NavLink>
          </li>
          <li>
            <NavLink className={navLinkClass} to="/work" onClick={onNavigate}>Our Work</NavLink>
          </li>
          <li>
            <NavLink className={navLinkClass} to="/about" onClick={onNavigate}>About</NavLink>
          </li>
          <li>
            <button
              type="button"
              className="nav-overlay-item nav-overlay-item--dark nav-overlay-item--contact-btn"
              onClick={onContactClick}
            >
              Contact
            </button>
          </li>
        </ul>
      )}

      {showContact && (
        <>
          <button
            type="button"
            className="nav-contact__back"
            onClick={onBack}
            aria-label="Back to menu"
          >
            <svg width="44" height="28" viewBox="0 0 44 28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="14" x2="37" y2="14" />
              <polyline points="29 5 38 14 29 23" />
            </svg>
          </button>
          <NavContactForm />
        </>
      )}
    </div>
  );
};

/* ── NavBar ── */
const NavBar = () => {
  const [menuOpen, toggleMenuOverlay] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    toggleMenuOverlay(false);
    setShowContact(false);
  }, [location.pathname]);

  const rafRef = useRef();
  const checkScroll = useCallback(() => {
    const y = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    setScrolled(y > 30);
    rafRef.current = requestAnimationFrame(checkScroll);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(checkScroll);
    return () => cancelAnimationFrame(rafRef.current);
  }, [checkScroll]);

  // Toggle body class to blur page content behind the nav
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("nav-open");
    } else {
      document.body.classList.remove("nav-open");
    }
    return () => document.body.classList.remove("nav-open");
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        if (showContact) {
          setShowContact(false);
        } else {
          toggleMenuOverlay(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen, showContact]);

  const handleContactClick = () => {
    // On mobile, just navigate to the contact page
    if (window.innerWidth <= MOBILE_BP) {
      toggleMenuOverlay(false);
      window.location.href = "/contact";
      return;
    }
    // On desktop, expand the panel and show the form
    setShowContact(true);
  };

  const handleClose = () => {
    toggleMenuOverlay(false);
    setShowContact(false);
  };

  return (
    <div className={classnames("nav-bar", {
      "scrolled": scrolled,
      "menu-open": menuOpen,
      "menu-contact": showContact,
    })}>
      <div className="container d-flex justify-content-between">
        <NavLink to="/" aria-label="Home">
          <img className="nav-logo" src={AltLogo} alt="Alvarez Branding Co." />
        </NavLink>
        <button
          type="button"
          className="btn p-0 nav-hamburger-btn"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => menuOpen ? handleClose() : toggleMenuOverlay(true)}
        >
          <img
            className={classnames("hamburger-menu", { "hamburger-menu--light": menuOpen })}
            src={menuOpen ? HamburgerMenuClose : HamburgerMenu}
            alt=""
          />
        </button>
      </div>
      {menuOpen && (
        <div
          className="nav-backdrop"
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      <NavOverlay
        menuOpen={menuOpen}
        showContact={showContact}
        onNavigate={() => toggleMenuOverlay(false)}
        onContactClick={handleContactClick}
        onBack={() => setShowContact(false)}
      />
    </div>
  );
};

export default NavBar;
